import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp, ROLES } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  File,
  X,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Columns,
  RefreshCw,
} from 'lucide-react';
import {
  getDownlineUserIds,
  CASE_STATUSES,
  defaultProducts,
  defaultVerificationTypes,
  defaultProductVerificationMappings,
} from '../../data/mockData';
import { fetchCases, fetchUsers, fetchClients, fetchProducts } from '../../api';

const sampleFields = [
  'Case Reference',
  'Applicant Name',
  'Phone Number',
  'Email Address',
  'Address Line 1',
  'Address Line 2',
  'City',
  'State',
  'Pincode',
  'Product Type',
  'Priority',
];

const systemFields = [
  { key: 'refNumber', label: 'Case Reference' },
  { key: 'applicantName', label: 'Applicant Name' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'email', label: 'Email Address' },
  { key: 'address', label: 'Full Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'product', label: 'Product Name' },
  { key: 'verificationType', label: 'Verification Type' },
  { key: 'status', label: 'Case Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignedTo', label: 'Assigned Executive' },
  { key: 'managerName', label: 'Mapped Manager' },
  { key: 'supervisorName', label: 'Mapped Supervisor/QC' },
  { key: 'vendorName', label: 'Mapped Vendor' },
  { key: 'createdAt', label: 'Date Created' },
  { key: 'decisionStatus', label: 'Verification Decision' },
];

export default function ImportExport() {
  const { currentRole, currentUserId, hasPermission } = useApp(); // Need useApp here
  const isClient = currentRole === ROLES.CLIENT;
  const [importMethod, setImportMethod] = useState('standard'); // standard or dynamic
  const navigate = useNavigate();
  const isSupremeManager = currentRole === ROLES.SUPREME_MANAGER;
  const isManager = currentRole === ROLES.MANAGER;
  const isVendor = currentRole === ROLES.VENDOR;
  const isHierarchyManager = false; // All managers now have full import/export access
  const hideImport = isVendor || !hasPermission('cases', 'create');
  const [activeTab, setActiveTab] = useState(hideImport ? 'export' : 'import');
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [step, setStep] = useState(1);
  const [fieldMappings, setFieldMappings] = useState({});

  // Client 6-Step Importer States
  const [clientStep, setClientStep] = useState(1);
  const [clientProduct, setClientProduct] = useState('');
  const [clientVerificationTypes, setClientVerificationTypes] = useState([]);
  const [clientTemplateDownloaded, setClientTemplateDownloaded] = useState(false);
  const [clientFile, setClientFile] = useState(null);
  const [clientProgress, setClientProgress] = useState(0);
  const [clientDragActive, setClientDragActive] = useState(false);
  
  // Expanded export filters
  const [exportFilters, setExportFilters] = useState({
    client: 'all',
    product: 'all',
    verificationType: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: '',
    manager: 'all',
    supervisor: 'all',
    fe: 'all',
    vendor: 'all',
    city: 'all',
    state: 'all',
  });
  
  // Export columns selection states
  const [selectedExportFields, setSelectedExportFields] = useState(
    systemFields.map(f => f.key)
  );
  
  // Modal states for export simulator
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('xlsx'); // xlsx, csv, pdf
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExportSuccess, setIsExportSuccess] = useState(false);
  const [exportedCount, setExportedCount] = useState(0);
  
  const [templateName, setTemplateName] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);

  const [liveCases, setLiveCases] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [liveClients, setLiveClients] = useState([]);
  const [liveProducts, setLiveProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [casesRes, usersRes, clientsRes, productsRes] = await Promise.all([
          fetchCases().catch(() => []),
          fetchUsers().catch(() => []),
          fetchClients().catch(() => []),
          fetchProducts().catch(() => []),
        ]);
        if (isMounted) {
          if (Array.isArray(casesRes)) setLiveCases(casesRes);
          if (Array.isArray(usersRes)) setLiveUsers(usersRes);
          if (Array.isArray(clientsRes)) setLiveClients(clientsRes);
          if (Array.isArray(productsRes)) setLiveProducts(productsRes);
        }
      } catch (e) {
        console.error("Failed to load live data for import/export", e);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  // Hierarchy & scoping helpers
  const downlineUserIds = getDownlineUserIds(currentUserId || 'USR002');
  const relevantUsers = liveUsers.filter(u => 
    currentRole === ROLES.ADMIN || u.id === currentUserId || downlineUserIds.includes(u.id)
  );
  
  const managersList = relevantUsers.filter(u => u.role === 'manager');
  const supervisorsList = relevantUsers.filter(u => u.role === 'quality_checker');
  const fesList = relevantUsers.filter(u => u.role === 'field_executive');
  const vendorsList = relevantUsers.filter(u => u.role === 'vendor');
  
  const baseCases = liveCases;
  
  // Extract unique cities, states, and types from scoped cases
  const uniqueCities = Array.from(new Set(baseCases.map(c => c.city).filter(Boolean)));
  const uniqueStates = Array.from(new Set(baseCases.map(c => c.state).filter(Boolean)));
  const uniqueVerificationTypes = Array.from(new Set(baseCases.map(c => c.verificationType).filter(Boolean)));

  const exportFilteredCases = baseCases.filter(c => {
    if (exportFilters.client !== 'all' && c.client?.id !== exportFilters.client) return false;
    if (exportFilters.product !== 'all' && c.product?.id !== exportFilters.product) return false;
    if (exportFilters.verificationType !== 'all' && c.verificationType !== exportFilters.verificationType) return false;
    if (exportFilters.status !== 'all' && c.status !== exportFilters.status) return false;
    if (exportFilters.city !== 'all' && c.city !== exportFilters.city) return false;
    if (exportFilters.state !== 'all' && c.state !== exportFilters.state) return false;
    
    // Scoped filters
    if (exportFilters.fe !== 'all' && c.assignedTo?.id !== exportFilters.fe) return false;
    if (exportFilters.vendor !== 'all' && c.assignedTo?.id !== exportFilters.vendor) return false;
    
    if (exportFilters.manager !== 'all') {
      const mgrDownlines = getDownlineUserIds(exportFilters.manager);
      if (c.assignedTo?.id !== exportFilters.manager && !mgrDownlines.includes(c.assignedTo?.id)) return false;
    }
    if (exportFilters.supervisor !== 'all') {
      const supDownlines = getDownlineUserIds(exportFilters.supervisor);
      if (c.assignedTo?.id !== exportFilters.supervisor && !supDownlines.includes(c.assignedTo?.id)) return false;
    }
    
    // Dates
    if (exportFilters.dateFrom && new Date(c.createdAt) < new Date(exportFilters.dateFrom)) return false;
    if (exportFilters.dateTo && new Date(c.createdAt) > new Date(exportFilters.dateTo)) return false;
    
    return true;
  });

  const handleExportClick = (format) => {
    setExportFormat(format);
    setIsExportModalOpen(true);
    setIsExportSuccess(false);
    setExportProgress(0);
  };
  
  const triggerExportSimulation = () => {
    setIsExporting(true);
    setExportProgress(0);
    const interval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsExporting(false);
          setIsExportSuccess(true);
          setExportedCount(exportFilteredCases.length);
          return 100;
        }
        return prev + 20;
      });
    }, 150);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (file) => {
    setUploadedFile(file);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const toggleExportField = (fieldKey) => {
    setSelectedExportFields(prev => 
      prev.includes(fieldKey)
        ? prev.filter(k => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="import-export-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Import / Export</h1>
        <p className="text-sm text-slate-500 mt-1">Import cases from files or export data for reporting</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1">
          {!hideImport && (
            <TabsTrigger value="import" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
              <Upload className="w-4 h-4" />
              Import Cases
            </TabsTrigger>
          )}
          <TabsTrigger value="export" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="mt-6">
          {isClient && (
            <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Choose Import Method</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  onClick={() => setImportMethod('standard')}
                  className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                    importMethod === 'standard' 
                      ? 'border-blue-600 bg-blue-50/20 shadow-md ring-1 ring-blue-600/30' 
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      importMethod === 'standard' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <Upload className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">Standard Bulk Import (Default)</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 font-medium leading-relaxed">Quickly upload cases in bulk using a standard CSV/Excel format.</p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setImportMethod('dynamic')}
                  className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                    importMethod === 'dynamic' 
                      ? 'border-blue-600 bg-blue-50/20 shadow-md ring-1 ring-blue-600/30' 
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      importMethod === 'dynamic' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <RefreshCw className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">Dynamic Case Import Wizard</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 font-medium leading-relaxed">Configure custom rules, download product templates, and map verifications.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(isClient && importMethod === 'dynamic') ? (
            /* Client 6-Step Importer Wizard */
            <div className="space-y-6">
              {/* Step Header */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Dynamic Case Import Wizard</h3>
                    <p className="text-xs text-slate-500 mt-1">Configure, download custom template, validate, and register cases</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400">Progress:</span>
                    <Badge className="bg-blue-50 border-blue-200 text-blue-700 font-bold">{clientStep} of 6 Steps</Badge>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${(clientStep / 6) * 100}%` }}
                  />
                </div>

                {/* Step indicators */}
                <div className="grid grid-cols-6 gap-2 mt-4 text-[10px] md:text-xs font-medium text-center text-slate-400">
                  <span className={clientStep >= 1 ? "text-blue-600 font-semibold" : ""}>1. Product</span>
                  <span className={clientStep >= 2 ? "text-blue-600 font-semibold" : ""}>2. Verif Type</span>
                  <span className={clientStep >= 3 ? "text-blue-600 font-semibold" : ""}>3. Template</span>
                  <span className={clientStep >= 4 ? "text-blue-600 font-semibold" : ""}>4. Upload</span>
                  <span className={clientStep >= 5 ? "text-blue-600 font-semibold" : ""}>5. Preview</span>
                  <span className={clientStep >= 6 ? "text-blue-600 font-semibold" : ""}>6. Submit</span>
                </div>
              </div>

              {/* Step 1: Select Product */}
              {clientStep === 1 && (
                <div className="crm-card p-6 space-y-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Step 1: Select Loan / Card Product</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { id: 'DP001', name: 'Credit Card', code: 'CC', desc: 'Verification for premium and standard card products' },
                      { id: 'DP003', name: 'Personal Loan', code: 'PL', desc: 'Physical and documents check for unsecured loans' },
                      { id: 'DP004', name: 'Home Loan', code: 'HL', desc: 'Detailed site and structural check for mortgages' },
                      { id: 'DP002', name: 'Mudra Loan Check', code: 'CCML', desc: 'Business verification under Cash Credit scheme' },
                      { id: 'DP005', name: 'Car Loan', code: 'CL', desc: 'Asset existence and residence verification' },
                      { id: 'DP009', name: 'Business Loan', code: 'BL', desc: 'Corporate address, legitimacy and office audits' }
                    ].map(p => (
                      <div 
                        key={p.id}
                        onClick={() => {
                          setClientProduct(p.id);
                          setClientStep(2);
                        }}
                        className={`crm-card p-4 cursor-pointer transition-all duration-200 border hover:shadow-md hover:border-blue-500 flex flex-col justify-between h-32 ${
                          clientProduct === p.id ? 'border-blue-600 bg-blue-50/20 shadow-sm' : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div>
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-slate-900 text-sm">{p.name}</span>
                            <Badge className="bg-slate-100 text-slate-700 text-[10px] uppercase font-semibold">{p.code}</Badge>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{p.desc}</p>
                        </div>
                        <div className="text-[11px] text-blue-600 font-semibold flex items-center justify-end gap-1">
                          Select Product
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Select Verification Type */}
              {clientStep === 2 && (
                <div className="crm-card p-6 space-y-6 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      Step 2: Select Required Verification Actions
                    </h4>
                    <span className="text-xs text-slate-500">
                      Product: <span className="font-semibold text-slate-800">
                        {['Credit Card', 'Mudra Loan Check', 'Personal Loan', 'Home Loan', 'Car Loan', 'Business Loan'][['DP001', 'DP002', 'DP003', 'DP004', 'DP005', 'DP009'].indexOf(clientProduct)] || 'Product'}
                      </span>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { id: 'VT001', name: 'Residence Verification', desc: 'Verify physical presence and living standards at home' },
                      { id: 'VT002', name: 'Office Verification', desc: 'Confirm workplace, cubicle, and employment proof' },
                      { id: 'VT003', name: 'Business Verification', desc: 'Audit physical shop, stocks, registry and business active state' },
                      { id: 'VT004', name: 'Aadhaar Check', desc: 'Perform offline QR verification of Aadhaar Card' },
                      { id: 'VT005', name: 'PAN Check', desc: 'Validate registration against income tax system' },
                      { id: 'VT006', name: 'Salary Slip Check', desc: 'Verify income statements and bank stamp legitimacy' },
                      { id: 'VT009', name: 'Document Verification', desc: 'Verify property maps, deeds, gas connection papers, etc.' },
                    ].map(vt => {
                      const isSelected = clientVerificationTypes.includes(vt.id);
                      return (
                        <div 
                          key={vt.id}
                          onClick={() => {
                            if (isSelected) {
                              setClientVerificationTypes(prev => prev.filter(x => x !== vt.id));
                            } else {
                              setClientVerificationTypes(prev => [...prev, vt.id]);
                            }
                          }}
                          className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 h-28 flex flex-col justify-between ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50/20 shadow-sm' 
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox 
                              checked={isSelected}
                              id={`chk-${vt.id}`}
                              className="mt-0.5 border-slate-300"
                            />
                            <div>
                              <Label htmlFor={`chk-${vt.id}`} className="font-semibold text-slate-800 text-xs block cursor-pointer select-none">
                                {vt.name}
                              </Label>
                              <p className="text-[10px] text-slate-400 mt-1 leading-snug">{vt.desc}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      onClick={() => setClientStep(1)}
                      className="border-slate-200 text-xs h-9"
                    >
                      Back: Select Product
                    </Button>
                    <Button 
                      onClick={() => setClientStep(3)}
                      disabled={clientVerificationTypes.length === 0}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1"
                    >
                      Next: Build Template
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Download Dynamic Template */}
              {clientStep === 3 && (
                <div className="crm-card p-6 space-y-6 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      Step 3: Dynamic Template Preview
                    </h4>
                    <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                      Auto-Compiled {getDynamicColumns().length} Fields
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Dynamic Excel Layout Grid Preview</h5>
                    
                    <div className="overflow-x-auto border border-slate-200 rounded-md">
                      <table className="w-full text-left border-collapse text-[11px]">
                        <thead>
                          <tr className="bg-slate-100 text-slate-700 border-b border-slate-200 font-semibold">
                            {getDynamicColumns().map((col, idx) => (
                              <th key={idx} className="p-2 border-r border-slate-200 whitespace-nowrap">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-100 text-slate-500">
                            {getDynamicColumns().map((col, idx) => (
                              <td key={idx} className="p-2 border-r border-slate-200 whitespace-nowrap bg-white">
                                {col === 'Reference ID' ? 'REF-001' : 
                                 col === 'Applicant Name' ? 'Rajesh Patel' :
                                 col === 'Phone Number' ? '9876543210' :
                                 col === 'Address' ? 'B-102, Shivalik Plaza' :
                                 col === 'City' ? 'Ahmedabad' :
                                 col === 'State' ? 'Gujarat' :
                                 col === 'Pincode' ? '380015' :
                                 col === 'Company Name' ? 'TCS Ltd' :
                                 col === 'Designation' ? 'Software Engineer' :
                                 col === 'GSTIN' ? '24AAAAT8932C1Z2' :
                                 col === 'Business Name' ? 'Patel Stores' :
                                 col === 'Identity ID Type' ? 'Aadhaar' :
                                 col === 'Identity ID Number' ? '1234-5678-9012' :
                                 col === 'Monthly Income' ? '₹75,000' : 'Dummy Data'}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div 
                      onClick={() => {
                        setClientTemplateDownloaded(true);
                        toast.success("Dynamic Excel template generated and downloaded successfully!");
                      }}
                      className="border border-slate-200 hover:border-emerald-500 p-4 rounded-xl cursor-pointer hover:shadow-sm transition-all duration-200 flex items-center gap-4 bg-white"
                    >
                      <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center border border-emerald-100">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <span className="font-bold text-slate-800 text-xs block">Download Excel Template</span>
                        <p className="text-[10px] text-slate-400 mt-1">Pre-formatted grid tailored for Excel uploads</p>
                      </div>
                    </div>

                    <div 
                      onClick={() => {
                        setClientTemplateDownloaded(true);
                        toast.success("Dynamic CSV template generated and downloaded successfully!");
                      }}
                      className="border border-slate-200 hover:border-blue-500 p-4 rounded-xl cursor-pointer hover:shadow-sm transition-all duration-200 flex items-center gap-4 bg-white"
                    >
                      <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <span className="font-bold text-slate-800 text-xs block">Download CSV Template</span>
                        <p className="text-[10px] text-slate-400 mt-1">Lightweight comma-separated layout</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      onClick={() => setClientStep(2)}
                      className="border-slate-200 text-xs h-9 bg-transparent"
                    >
                      Back: Select Types
                    </Button>
                    <Button 
                      onClick={() => setClientStep(4)}
                      disabled={!clientTemplateDownloaded}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1 shadow-md shadow-blue-500/10"
                    >
                      Next: Upload Leads File
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Upload File */}
              {clientStep === 4 && (
                <div className="crm-card p-6 space-y-6 bg-white">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Step 4: Upload Compiled Verification Sheet</h4>

                  {/* Drag and Drop Zone */}
                  <div
                    className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
                      clientDragActive 
                        ? 'border-blue-500 bg-blue-50/20' 
                        : 'border-slate-200 hover:border-slate-300 bg-slate-50/30'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setClientDragActive(true); }}
                    onDragLeave={() => setClientDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setClientDragActive(false);
                      if (e.dataTransfer.files?.[0]) {
                        const file = e.dataTransfer.files[0];
                        setClientFile(file);
                        setClientProgress(0);
                        const interval = setInterval(() => {
                          setClientProgress(prev => {
                            if (prev >= 100) { clearInterval(interval); return 100; }
                            return prev + 25;
                          });
                        }, 100);
                      }
                    }}
                    onClick={() => document.getElementById('client-file-input')?.click()}
                  >
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                    <span className="font-semibold text-slate-800 text-xs block">Drag & Drop leads sheet here or browse</span>
                    <p className="text-[10px] text-slate-400 mt-1.5">Supports strictly .csv, .xls, .xlsx formats. Max 15MB.</p>
                    <input 
                      type="file" 
                      id="client-file-input"
                      className="hidden" 
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          const file = e.target.files[0];
                          setClientFile(file);
                          setClientProgress(0);
                          const interval = setInterval(() => {
                            setClientProgress(prev => {
                              if (prev >= 100) { clearInterval(interval); return 100; }
                              return prev + 25;
                            });
                          }, 80);
                        }
                      }}
                    />
                  </div>

                  {clientFile && (
                    <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-center">
                          <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800 text-xs block truncate max-w-[250px]">{clientFile.name}</span>
                          <span className="text-[10px] text-slate-400">{(clientFile.size / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>

                      {clientProgress < 100 ? (
                        <div className="w-24 space-y-1">
                          <Progress value={clientProgress} className="h-1" />
                          <span className="text-[9px] text-slate-400 block text-right font-medium">{clientProgress}% uploaded</span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Ready
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      onClick={() => setClientStep(3)}
                      className="border-slate-200 text-xs h-9 bg-transparent"
                    >
                      Back: Download Template
                    </Button>
                    <Button 
                      onClick={() => setClientStep(5)}
                      disabled={!clientFile || clientProgress < 100}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1"
                    >
                      Next: Validate File
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Validate Preview Stats */}
              {clientStep === 5 && (
                <div className="crm-card p-6 space-y-6 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      Step 5: Pre-Import File Validation Dashboard
                    </h4>
                    <span className="text-xs text-slate-400 font-medium">Sheet: {clientFile?.name || 'leads.xlsx'}</span>
                  </div>

                  {/* Stat Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Total Cases Loaded</span>
                      <span className="text-2xl font-bold text-slate-800 mt-2 block">6</span>
                    </div>

                    <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/10">
                      <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide block">Clean Success Cases</span>
                      <span className="text-2xl font-bold text-emerald-700 mt-2 block">5</span>
                    </div>

                    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Duplicate Entries</span>
                      <span className="text-2xl font-bold text-slate-800 mt-2 block">0</span>
                    </div>

                    <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/20">
                      <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide block">Invalid Row Warnings</span>
                      <span className="text-2xl font-bold text-amber-700 mt-2 block">1</span>
                    </div>
                  </div>

                  {/* Warning Details */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-amber-800 text-xs block">File Integrity Warnings Detected</span>
                      <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                        Row 5: The applicant cell contains a blank name. This row will be completely skipped during register to prevent database corruption.
                      </p>
                    </div>
                  </div>

                  {/* Cases Preview Table */}
                  <div className="space-y-2">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Preview Clean Cases to Import</span>
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-left border-collapse text-[11px]">
                        <thead>
                          <tr className="bg-slate-100 text-slate-700 border-b border-slate-200 font-semibold">
                            <th className="p-2 border-r border-slate-200">Ref ID</th>
                            <th className="p-2 border-r border-slate-200">Applicant Name</th>
                            <th className="p-2 border-r border-slate-200">Phone</th>
                            <th className="p-2 border-r border-slate-200">Address</th>
                            <th className="p-2 border-r border-slate-200">City</th>
                            <th className="p-2 border-r border-slate-200">Pincode</th>
                            {getDynamicColumns().includes('Company Name') && <th className="p-2 border-r border-slate-200">Company</th>}
                            {getDynamicColumns().includes('GSTIN') && <th className="p-2 border-r border-slate-200">GSTIN</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {simulatedRows.map((r, idx) => (
                            <tr key={idx} className="border-b border-slate-100 text-slate-600 bg-white">
                              <td className="p-2 border-r border-slate-200 font-semibold text-blue-600">{r.ref}</td>
                              <td className="p-2 border-r border-slate-200 font-medium text-slate-900">{r.name}</td>
                              <td className="p-2 border-r border-slate-200">{r.phone}</td>
                              <td className="p-2 border-r border-slate-200 truncate max-w-[150px]">{r.address}</td>
                              <td className="p-2 border-r border-slate-200">{r.city}</td>
                              <td className="p-2 border-r border-slate-200">{r.pincode}</td>
                              {getDynamicColumns().includes('Company Name') && <td className="p-2 border-r border-slate-200">{r.extra1}</td>}
                              {getDynamicColumns().includes('GSTIN') && <td className="p-2 border-r border-slate-200">{r.extra2}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      onClick={() => setClientStep(4)}
                      className="border-slate-200 text-xs h-9 bg-transparent"
                    >
                      Back: Re-upload File
                    </Button>
                    <Button 
                      onClick={() => setClientStep(6)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1 shadow-md shadow-blue-500/10"
                    >
                      Next: Confirm Register
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 6: Submit Import */}
              {clientStep === 6 && (
                <div className="crm-card p-6 space-y-6 bg-white">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Step 6: Register & Submit Cases</h4>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                    <div className="flex justify-between items-center text-xs border-b border-slate-200 pb-3">
                      <span className="text-slate-500">Client Profile:</span>
                      <span className="font-semibold text-slate-800">HDFC Bank (CLT001)</span>
                    </div>

                    <div className="flex justify-between items-center text-xs border-b border-slate-200 pb-3">
                      <span className="text-slate-500">Product Mapping:</span>
                      <span className="font-semibold text-slate-800">
                        {['Credit Card', 'Mudra Loan Check', 'Personal Loan', 'Home Loan', 'Car Loan', 'Business Loan'][['DP001', 'DP002', 'DP003', 'DP004', 'DP005', 'DP009'].indexOf(clientProduct)] || 'Product'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-xs border-b border-slate-200 pb-3">
                      <span className="text-slate-500">Actions Configured:</span>
                      <span className="font-semibold text-slate-800">{clientVerificationTypes.length} verification types</span>
                    </div>

                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Total Cases to Add:</span>
                      <span className="font-bold text-emerald-600 text-sm">5 cases</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-xs font-semibold text-slate-700">Pre-processing Instructions</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1.5">
                      <div className="flex items-center gap-2">
                        <Checkbox id="client-dup" defaultChecked />
                        <Label htmlFor="client-dup" className="text-xs text-slate-600 block cursor-pointer">Skip duplicate Reference IDs</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="client-notif" defaultChecked />
                        <Label htmlFor="client-notif" className="text-xs text-slate-600 block cursor-pointer">Notify Universal QC instantly</Label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      onClick={() => setClientStep(5)}
                      className="border-slate-200 text-xs h-9 bg-transparent"
                    >
                      Back: Preview Stats
                    </Button>
                    <Button 
                      onClick={() => {
                        toast.success("Successfully registered 5 new verification cases!");
                        setTimeout(() => {
                          navigate('/cases');
                        }, 800);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-9 gap-1.5 shadow-md shadow-emerald-500/10"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Register Cases Now
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Existing 3-Step Importer for Admins/Managers AND Client Standard Import */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload Section */}
              <div className="lg:col-span-2 space-y-6">
                {/* Step Indicator */}
                <div className="flex items-center gap-4 mb-6">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step >= s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
                      </div>
                      <span className={`ml-2 text-sm ${step >= s ? 'text-slate-900' : 'text-slate-500'}`}>
                        {s === 1 ? 'Upload File' : s === 2 ? 'Map Fields' : 'Review & Import'}
                      </span>
                      {s < 3 && <ArrowRight className="w-4 h-4 mx-4 text-slate-400" />}
                    </div>
                  ))}
                </div>

                {step === 1 && (
                  <div className="crm-card p-6">
                    {/* Drag & Drop Zone */}
                    <div
                      className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                        dragActive 
                          ? 'border-indigo-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      data-testid="drop-zone"
                    >
                      <Upload className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        Drop your file here
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Supports Excel (.xlsx, .xls), CSV, and PDF files
                      </p>
                      <label htmlFor="file-upload">
                        <Button asChild className="bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          <span>
                            Browse Files
                            <input
                              id="file-upload"
                              type="file"
                              className="hidden"
                              accept=".xlsx,.xls,.csv,.pdf"
                              onChange={handleFileInput}
                            />
                          </span>
                        </Button>
                      </label>
                    </div>

                    {/* Uploaded File */}
                    {uploadedFile && (
                      <div className="mt-6 p-4 rounded-lg bg-white">
                        <div className="flex items-center gap-4">
                          <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{uploadedFile.name}</p>
                            <p className="text-xs text-slate-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setUploadedFile(null)}
                            className="text-slate-500 hover:text-slate-900"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {uploadProgress < 100 && (
                          <div className="mt-3">
                            <Progress value={uploadProgress} className="h-1" />
                            <p className="text-xs text-slate-500 mt-1">{uploadProgress}% uploaded</p>
                          </div>
                        )}
                        {uploadProgress === 100 && (
                          <div className="flex items-center gap-2 mt-3 text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm font-medium">File uploaded successfully</span>
                          </div>
                        )}
                      </div>
                    )}

                    {uploadedFile && uploadProgress === 100 && (
                      <div className="flex justify-end mt-6">
                        <Button 
                          onClick={() => setStep(2)} 
                          className="bg-blue-600 hover:bg-blue-700 gap-2"
                          data-testid="next-step-btn"
                        >
                          Next: Map Fields
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div className="crm-card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-slate-900">Field Mapping</h3>
                      <Button variant="outline" size="sm" className="bg-white border-slate-200 gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Auto-detect
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 pb-3 border-b border-slate-200">
                        <span className="text-sm font-medium text-slate-500">File Column</span>
                        <span className="text-sm font-medium text-slate-500">Map to System Field</span>
                      </div>
                      {sampleFields.map((field, index) => (
                        <div key={field} className="grid grid-cols-2 gap-4 items-center">
                          <div className="flex items-center gap-2">
                            <Columns className="w-4 h-4 text-slate-500" />
                            <span className="text-sm text-slate-900">{field}</span>
                          </div>
                          <Select
                            value={fieldMappings[field] || ''}
                            onValueChange={(value) => setFieldMappings(prev => ({ ...prev, [field]: value }))}
                          >
                            <SelectTrigger className="bg-slate-50/50 border-slate-200">
                              <SelectValue placeholder="Select field..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              <SelectItem value="skip">-- Skip --</SelectItem>
                              {systemFields.map((sf) => (
                                <SelectItem key={sf.key} value={sf.key}>{sf.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between mt-8">
                      <Button variant="outline" onClick={() => setStep(1)} className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-transparent">
                        Back
                      </Button>
                      <Button onClick={() => setStep(3)} className="bg-blue-600 hover:bg-blue-700 gap-2">
                        Next: Review
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="crm-card p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Review Import</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-lg bg-white">
                        <span className="text-sm text-slate-500">Total Records</span>
                        <span className="text-sm font-medium text-slate-900">156</span>
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-800 font-medium">Valid Records</span>
                        </div>
                        <span className="text-sm font-semibold text-emerald-700">152</span>
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 border border-amber-100">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-amber-800 font-medium">Records with Warnings</span>
                        </div>
                        <span className="text-sm font-semibold text-amber-700">4</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Label className="text-sm text-slate-500">Import Options</Label>
                      <div className="space-y-3 mt-3">
                        <div className="flex items-center gap-2">
                          <Checkbox id="skipDuplicates" defaultChecked />
                          <label htmlFor="skipDuplicates" className="text-sm text-slate-700">Skip duplicate records</label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="autoAssign" />
                          <label htmlFor="autoAssign" className="text-sm text-slate-700">Auto-assign to available FEs</label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="sendNotifications" defaultChecked />
                          <label htmlFor="sendNotifications" className="text-sm text-slate-700">Send notifications after import</label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8">
                      <Button variant="outline" onClick={() => setStep(2)} className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-transparent">
                        Back
                      </Button>
                      <Button 
                        onClick={() => {
                          toast.success("Successfully imported 152 new validation cases!");
                          setTimeout(() => navigate('/cases'), 800);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 gap-2" 
                        data-testid="start-import-btn"
                      >
                        <Upload className="w-4 h-4" />
                        Start Import
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="crm-card p-6">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Download Templates</h4>
                  <div className="space-y-3">
                    <Button variant="outline" className="w-full justify-start bg-white border-slate-200 gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                      Excel Template
                    </Button>
                    <Button variant="outline" className="w-full justify-start bg-white border-slate-200 gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      CSV Template
                    </Button>
                  </div>
                </div>

                <div className="crm-card p-6">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Import Tips</h4>
                  <ul className="space-y-2 text-sm text-slate-500">
                    <li className="flex gap-2">
                      <span className="text-blue-600">•</span>
                      Ensure all required fields are filled
                    </li>
                    <li className="flex gap-2">
                      <span className="text-blue-600">•</span>
                      Phone numbers should include country code
                    </li>
                    <li className="flex gap-2">
                      <span className="text-blue-600">•</span>
                      Date format: DD/MM/YYYY
                    </li>
                    <li className="flex gap-2">
                      <span className="text-blue-600">•</span>
                      Maximum file size: 10MB
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 crm-card p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Export Filters</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {!isClient && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500">Client</Label>
                    <Select
                      value={exportFilters.client}
                      onValueChange={(v) => setExportFilters(prev => ({ ...prev, client: v }))}
                    >
                      <SelectTrigger className="bg-slate-50/50 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Clients</SelectItem>
                        {liveClients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Product</Label>
                  <Select
                    value={exportFilters.product}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, product: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Products</SelectItem>
                      {liveProducts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Verification Type</Label>
                  <Select
                    value={exportFilters.verificationType}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, verificationType: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Verification Types</SelectItem>
                      {uniqueVerificationTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Case Status</Label>
                  <Select
                    value={exportFilters.status}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value={CASE_STATUSES.COMPLETED}>Completed</SelectItem>
                      <SelectItem value={CASE_STATUSES.IN_PROGRESS}>In Progress</SelectItem>
                      <SelectItem value={CASE_STATUSES.QC_PENDING}>QC Pending</SelectItem>
                      <SelectItem value={CASE_STATUSES.QC_REJECTED}>QC Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Mapped Manager</Label>
                  <Select
                    value={exportFilters.manager}
                    disabled={currentRole === ROLES.MANAGER}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, manager: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Mapped Managers</SelectItem>
                      {managersList.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Mapped Supervisor/QC</Label>
                  <Select
                    value={exportFilters.supervisor}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, supervisor: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Mapped Supervisors</SelectItem>
                      {supervisorsList.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Assigned FE</Label>
                  <Select
                    value={exportFilters.fe}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, fe: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Assigned FEs</SelectItem>
                      {fesList.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Mapped Vendor</Label>
                  <Select
                    value={exportFilters.vendor}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, vendor: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Mapped Vendors</SelectItem>
                      {vendorsList.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">City</Label>
                  <Select
                    value={exportFilters.city}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, city: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Cities</SelectItem>
                      {uniqueCities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">State</Label>
                  <Select
                    value={exportFilters.state}
                    onValueChange={(v) => setExportFilters(prev => ({ ...prev, state: v }))}
                  >
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All States</SelectItem>
                      {uniqueStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Date From</Label>
                  <Input
                    type="date"
                    value={exportFilters.dateFrom}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    className="bg-slate-50/50 border-slate-200"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500">Date To</Label>
                  <Input
                    type="date"
                    value={exportFilters.dateTo}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    className="bg-slate-50/50 border-slate-200"
                  />
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm text-slate-500">Select Fields to Export</Label>
                  {savedTemplates.length > 0 && (
                    <Select onValueChange={(v) => {
                      const tpl = savedTemplates.find(t => t.name === v);
                      if(tpl) setSelectedExportFields(tpl.fields);
                    }}>
                      <SelectTrigger className="w-48 bg-white border-slate-200">
                        <SelectValue placeholder="Load Saved Template" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        {savedTemplates.map((t, idx) => (
                          <SelectItem key={idx} value={t.name}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {systemFields.map((field) => (
                    <div key={field.key} className="flex items-center gap-2">
                      <Checkbox
                        id={field.key}
                        checked={selectedExportFields.includes(field.key)}
                        onCheckedChange={() => toggleExportField(field.key)}
                      />
                      <label htmlFor={field.key} className="text-sm text-slate-700 cursor-pointer">
                        {field.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {[ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole) && (
                <div className="mt-6 flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <Label className="shrink-0 text-sm font-medium text-slate-700">Save as Template:</Label>
                  <Input 
                    value={templateName} 
                    onChange={e => setTemplateName(e.target.value)} 
                    placeholder="E.g., Vendor Billing Layout" 
                    className="bg-white border-slate-200 h-8 text-sm max-w-[250px]" 
                  />
                  <Button 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700 h-8 text-white font-medium" 
                    disabled={!templateName}
                    onClick={() => {
                        setSavedTemplates(prev => [...prev, { name: templateName, fields: selectedExportFields }]);
                        setTemplateName('');
                    }}
                  >
                    Save Template
                  </Button>
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <Button onClick={() => handleExportClick('xlsx')} className="bg-blue-600 hover:bg-blue-700 gap-2 text-white font-medium" data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4" />
                  Export as Excel
                </Button>
                <Button onClick={() => handleExportClick('csv')} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 gap-2 bg-white font-medium" data-testid="export-csv-btn">
                  <FileText className="w-4 h-4" />
                  Export as CSV
                </Button>
                <Button onClick={() => handleExportClick('pdf')} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 gap-2 bg-white font-medium" data-testid="export-pdf-btn">
                  <File className="w-4 h-4" />
                  Export as PDF
                </Button>
              </div>
            </div>

            <div className="crm-card p-6 h-fit space-y-4">
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Export Preview</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-sm text-slate-500">Hierarchy Scope</span>
                  <span className="text-sm font-semibold text-slate-800">
                    {isSupremeManager ? 'Your Downline Only' : (isManager ? 'Manager Downline' : 'Global Admin')}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-sm text-slate-500">Filtered Records</span>
                  <span className="text-sm font-semibold text-slate-900">{exportFilteredCases.length}</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-sm text-slate-500">Fields Selected</span>
                  <span className="text-sm font-semibold text-slate-900">{selectedExportFields.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Est. Workbook Size</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {(exportFilteredCases.length * selectedExportFields.length * 0.1).toFixed(1)} KB
                  </span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Pre-Export Columns Selector Checklist Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-150">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="font-semibold text-slate-800 text-lg">Export Column Pre-check</h3>
                <p className="text-xs text-slate-500 mt-0.5">Scoping {exportFilteredCases.length} cases for export</p>
              </div>
              <button onClick={() => setIsExportModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Format selection info */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-sm">
                <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                <div>
                  <span className="font-medium">Selected Format: </span>
                  {exportFormat.toUpperCase()} File Extension
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-700">Choose Columns to Include ({selectedExportFields.length}/18)</span>
                  <button 
                    onClick={() => {
                      if (selectedExportFields.length === systemFields.length) {
                        setSelectedExportFields([]);
                      } else {
                        setSelectedExportFields(systemFields.map(f => f.key));
                      }
                    }} 
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {selectedExportFields.length === systemFields.length ? 'Deselect All' : 'Select All 18 Fields'}
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-lg border border-slate-100">
                  {systemFields.map((field) => (
                    <div key={field.key} className="flex items-center gap-2.5 p-1">
                      <Checkbox
                        id={`modal-${field.key}`}
                        checked={selectedExportFields.includes(field.key)}
                        onCheckedChange={() => toggleExportField(field.key)}
                      />
                      <label htmlFor={`modal-${field.key}`} className="text-sm font-medium text-slate-700 cursor-pointer select-none">
                        {field.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Progress bar when simulating export */}
              {isExporting && (
                <div className="space-y-2 p-4 bg-slate-50 border border-slate-200 rounded-lg animate-pulse">
                  <div className="flex justify-between text-xs font-semibold text-slate-600">
                    <span>Building downline data matrix...</span>
                    <span>{exportProgress}%</span>
                  </div>
                  <Progress value={exportProgress} className="h-2 bg-slate-200 [&>div]:bg-blue-600" />
                </div>
              )}

              {isExportSuccess && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 space-y-2">
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span>Export generated successfully!</span>
                  </div>
                  <p className="text-xs text-emerald-700">
                    Scoped strictly under your hierarchy downline. Total {exportedCount} cases verified and compiled with {selectedExportFields.length} columns.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
              <Button variant="outline" onClick={() => setIsExportModalOpen(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-white">
                Cancel
              </Button>
              {!isExportSuccess ? (
                <Button 
                  onClick={triggerExportSimulation} 
                  disabled={selectedExportFields.length === 0 || isExporting} 
                  className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                >
                  {isExporting ? 'Generating...' : 'Confirm & Export'}
                </Button>
              ) : (
                <Button 
                  onClick={() => setIsExportModalOpen(false)} 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
